import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Layout from '../AppLayout';

const PageContainer = styled.div`
  position: relative;
  z-index: 10;
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 60px;
  padding: 40px 20px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const StatItem = styled(motion.div)`
  text-align: center;
`;

const StatNumber = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 48px;
  font-weight: 700;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-top: 5px;
`;

const HeroTitle = styled(motion.h1)`
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(36px, 8vw, 80px);
  font-weight: 900;
  background: linear-gradient(135deg, #ffffff 0%, var(--primary) 50%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 20px;
  text-shadow: 0 0 60px rgba(0, 240, 255, 0.5);
`;

const HeroSubtitle = styled.p`
  font-size: clamp(16px, 3vw, 24px);
  color: var(--text-secondary);
  max-width: 700px;
  margin-bottom: 50px;
  line-height: 1.8;
`;

const HeroSection = styled.section`
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  position: relative;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1200px;
  width: 100%;
  padding: 40px 20px;
`;

const FeatureCard = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 40px 30px;
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, var(--primary), var(--accent));
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    border-color: var(--primary);
    box-shadow: var(--glow-primary);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const FeatureIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  display: inline-block;
`;

const FeatureTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 20px;
  color: var(--primary);
  margin-bottom: 15px;
`;

const FeatureDesc = styled.p`
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.7;
`;

const SectionTitle = styled(motion.h2)`
  font-family: 'Orbitron', sans-serif;
  font-size: 32px;
  text-align: center;
  margin-bottom: 20px;
  color: var(--text-primary);
  
  span {
    color: var(--primary);
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  color: var(--text-secondary);
  font-size: 16px;
  max-width: 600px;
  margin: 0 auto 50px;
`;

const CTABtn = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: #fff;
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 40px;
  border-radius: 30px;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 10px;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  border: none;
  
  &:hover {
    box-shadow: 0 0 30px rgba(0, 240, 255, 0.6);
  }
`;

const SecondaryBtn = styled(CTABtn)`
  background: transparent;
  border: 2px solid var(--primary);
  
  &:hover {
    background: rgba(0, 240, 255, 0.1);
  }
`;

const TechStack = styled.div`
  display: flex;
  gap: 40px;
  margin-top: 60px;
  flex-wrap: wrap;
  justify-content: center;
`;

const TechItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
  font-size: 14px;
  
  span:first-child {
    font-size: 32px;
  }
`;

const features = [
  {
    icon: '🎙️',
    title: '智能语音合成',
    desc: '基于深度学习的TTS技术，支持多语言、多音色、高自然度的语音生成'
  },
  {
    icon: '🎭',
    title: '声音克隆',
    desc: '仅需少量样本即可克隆个性化声音，保留声音特征和情感表达'
  },
  {
    icon: '🎬',
    title: '数字人视频',
    desc: 'AI驱动的数字人形象，配合唇形同步技术，生成逼真的视频内容'
  },
  {
    icon: '🌍',
    title: '多语言支持',
    desc: '支持中、英、日、韩、法、德等30+种语言的语音合成和转换'
  },
  {
    icon: '⚡',
    title: '实时合成',
    desc: '高效的渲染管线，支持实时或快速生成，大幅提升内容生产效率'
  },
  {
    icon: '🔗',
    title: 'API开放',
    desc: '提供完整的RESTful API接口，支持二次开发和企业级集成'
  }
];

const HomePage = () => {
  return (
    <Layout>
      <PageContainer>
        <HeroSection>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle>数字人AI合成系统</HeroTitle>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <HeroSubtitle>
              融合前沿AI技术，一键生成超写实数字人形象与高自然度语音
              <br />让创意无限，让表达更生动
            </HeroSubtitle>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <CTABtn as={Link} to="/video">
              开始创作
            </CTABtn>
            <SecondaryBtn as={Link} to="/voice">
              体验声音
            </SecondaryBtn>
          </motion.div>
          
          <StatsBar>
            <StatItem
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              <StatNumber>30+</StatNumber>
              <StatLabel>支持语言</StatLabel>
            </StatItem>
            <StatItem
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
            >
              <StatNumber>100+</StatNumber>
              <StatLabel>数字人形象</StatLabel>
            </StatItem>
            <StatItem
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
            >
              <StatNumber>99%</StatNumber>
              <StatLabel>合成准确率</StatLabel>
            </StatItem>
            <StatItem
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9 }}
            >
              <StatNumber>24/7</StatNumber>
              <StatLabel>在线服务</StatLabel>
            </StatItem>
          </StatsBar>
        </HeroSection>
        
        <section style={{ padding: '80px 20px', maxWidth: '1200px', margin: '0 auto' }}>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            核心<span>功能</span>
          </SectionTitle>
          
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            基于先进的深度学习模型，为您提供一站式数字人内容创作解决方案
          </SectionSubtitle>
          
          <FeatureGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.03 }}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDesc>{feature.desc}</FeatureDesc>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </section>
        
        <section style={{ 
          padding: '100px 20px', 
          background: 'linear-gradient(180deg, transparent, rgba(0, 240, 255, 0.05), transparent)',
          textAlign: 'center'
        }}>
          <SectionTitle>
            技术<span>栈</span>
          </SectionTitle>
          
          <TechStack>
            <TechItem>
              <span>🧠</span>
              <span>AI 深度学习</span>
            </TechItem>
            <TechItem>
              <span>🔊</span>
              <span>TTS 语音合成</span>
            </TechItem>
            <TechItem>
              <span>🎭</span>
              <span>数字人形象</span>
            </TechItem>
            <TechItem>
              <span>👄</span>
              <span>唇形同步</span>
            </TechItem>
            <TechItem>
              <span>☁️</span>
              <span>云端渲染</span>
            </TechItem>
            <TechItem>
              <span>⚡</span>
              <span>GPU 加速</span>
            </TechItem>
          </TechStack>
        </section>
      </PageContainer>
    </Layout>
  );
};

export default HomePage;
